import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import {
  AiGoalsReportInput,
  GenAiGoalsReportGQL,
  GetAiDeepInsightGQL,
  GetGoalsGQL,
  Goals,
  GoalsInput,
  GoalsReportDetails,
  GoalsReportDetailsGQL,
  UpdateAiDeepInsightReportGQL,
  UpdateGoalsGQL
} from '@cpd-app/generated';
import { Observable, throwError } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AIDeepInsights, AIGoalsReport, UserGoals } from '../../models';
import { getAiGoalsReportResponseParser } from '@cpd-app/core-module/mappers';
import {
  HttpClient,
  HttpDownloadProgressEvent,
  HttpEventType,
  HttpHeaders
} from '@angular/common/http';
import { environment } from '@cpd-app/environment';
import { ViaReportStatus } from '@cpd-app/shared-module/constants';

@Injectable({
  providedIn: 'root'
})
export class GoalDataService {
  constructor(
    private getGoalsGQL: GetGoalsGQL,
    private updateGoalsGQL: UpdateGoalsGQL,
    private goalsReportDetailsGQL: GoalsReportDetailsGQL,
    private genAiGoalsReportGQL: GenAiGoalsReportGQL,
    private updateAiDeepInsightReportGQL: UpdateAiDeepInsightReportGQL,
    private getAiDeepInsightGQL: GetAiDeepInsightGQL,
    private httpClient: HttpClient,
    private apollo: Apollo
  ) {}

  public getUserGoals(): Observable<UserGoals> {
    return this.apollo
      .query({ query: GOALS_WITH_CREDENTIAL, fetchPolicy: 'network-only' })
      .pipe(
        map((result) => (result?.data as { goals?: UserGoals })?.goals as UserGoals)
      );
  }

  public updateUserGoals(userGoals: GoalsInput): Observable<Goals> {
    const schoolId = userGoals.schoolId
      ? parseInt(userGoals.schoolId.toString(), 10)
      : null;
    return this.apollo
      .mutate({
        mutation: UPDATE_GOALS_WITH_CREDENTIAL,
        variables: {
        goals: {
          onetCode: userGoals.onetCode,
          jobId: userGoals.jobId ? userGoals.jobId.toString() : null,
          occupationTitle: userGoals.occupationTitle, // TODO: should not remove this field
          schoolId: schoolId,
          schoolProgramId: userGoals.schoolProgramId,
          schoolProgramEdLevelId: userGoals.schoolProgramEdLevelId,
          certificationId: userGoals.certificationId
            ? userGoals.certificationId.toString()
            : null,
          licenseId: userGoals.licenseId ? userGoals.licenseId : null,
          /*
           * CRED.1. Cast because `GoalsInput` in generated.ts predates these
           * two fields: codegen reads the schema from a RUNNING GATEWAY, and
           * the subgraph carrying them deploys in the same change as this file.
           *
           * The WRITE works regardless, which is why a cast is enough here and
           * the read below needed a whole document. The generated mutation
           * passes `$goals` through as one variable rather than enumerating its
           * fields, so extra keys reach the server untouched.
           *
           * Delete the cast the first time codegen runs against a dev gateway
           * carrying CRED.1.
           */
          credentialCtid: credentialFieldsOf(userGoals).credentialCtid || null,
          credentialTitle: credentialFieldsOf(userGoals).credentialTitle || null,
          customOnetCode: userGoals.customOnetCode || null,
          pathwayId: userGoals.pathwayId || null
        }
        }
      })
      .pipe(
        map(
          (result) =>
            (result.data as { updateGoals?: { goals?: Goals } })?.updateGoals
              ?.goals as Goals
        )
      );
  }

  public updateLearningSuggestions(
    conversationId: number,
    messageId: number
  ): Observable<AIDeepInsights> {
    return this.updateAiDeepInsightReportGQL
      .mutate({ conversationId, messageId })
      .pipe(
        map((results) => {
          return results?.data?.updateAIDeepInsightReport;
        })
      );
  }

  public getAiDeepInsight(): Observable<AIDeepInsights> {
    return this.getAiDeepInsightGQL.fetch().pipe(
      map((results) => {
        return results?.data?.getAiDeepInsight;
      })
    );
  }

  public genAiGoalsReport(
    surveyData: AiGoalsReportInput
  ): Observable<AIGoalsReport> {
    return this.genAiGoalsReportGQL.mutate({ surveyData }).pipe(
      map((results) => {
        return getAiGoalsReportResponseParser(results?.data?.genAiGoalsReport);
      })
    );
  }

  public getGoalsReportDetails(): Observable<GoalsReportDetails> {
    return this.goalsReportDetailsGQL.fetch().pipe(
      map((results) => {
        return results?.data?.goalsReportDetails;
      })
    );
  }

  public refreshViaInsights(
    surveyData: AiGoalsReportInput
  ): Observable<AIGoalsReport> {
    return this.genAiGoalsReportGQL
      .mutate({ surveyData, forceUpdate: true })
      .pipe(
        map((results) => {
          return getAiGoalsReportResponseParser(
            results?.data?.genAiGoalsReport
          );
        })
      );
  }

  public getGenViaInsightStatus(): Observable<ViaReportStatus | null> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Silent-Request': 'true',
      'Cache-Control': 'no-cache'
    });
    const streamUri = environment.genViaStatusUri;

    // AI-5. No URL means no stream, and posting to `undefined` fails somewhere
    // obscure (the auth interceptor, or a 404 from the dev server) after three
    // silent retries. Say so at once; the fallback poll in the goal effects
    // takes over and still brings the finished report in.
    if (!streamUri) {
      return throwError(
        () => new Error('The Via status stream is not configured for this environment.')
      );
    }

    return this.httpClient
      .post(
        streamUri,
        {},
        {
          headers,
          observe: 'events',
          responseType: 'text',
          reportProgress: true
        }
      )
      .pipe(
        filter(
          (event: HttpDownloadProgressEvent) =>
            event.type === HttpEventType.DownloadProgress && !!event.partialText
        ),
        map((event) => {
          return this.parseSSEData(event.partialText);
        })
      );
  }

  private parseSSEData(text: string): ViaReportStatus | null {
    const lines = text.split('\n\ndata: ').filter((i) => {
      return !!i?.trim();
    });

    /**
     * GR-4. THE LINE THAT MADE "Insights from Via" SPIN FOREVER.
     *
     * `lines[lines.length - 2]` was read without this guard. The status
     * endpoint writes its first frame the instant you connect and the next one
     * two seconds later, and HttpClient reports download progress as soon as
     * that first frame lands -- so the first call into here almost always sees
     * ONE line, `lines[-1]` is undefined, and `.replace` throws.
     *
     * The throw happens inside a `map`, so RxJS turns it into an error
     * notification rather than a crash. connectGenStatus$ catches it,
     * dispatches connectGenStatusFailure, the reducer sets statusConnect
     * false, and nothing re-arms. The member watches a spinner while the
     * backend finishes the report normally and files it in the database --
     * which is what it was doing the whole time.
     *
     * Returning null rather than the first status is deliberate. This emits
     * only when the status CHANGES, so the effect is not re-triggered by every
     * keepalive frame; with one frame there is nothing to compare against, so
     * there is no change to report. The page's own initial read establishes
     * the starting status.
     */
    if (lines.length < 2) {
      return null;
    }

    const lastLine = lines[lines.length - 1].replace(/data:/g, '');
    const prevLine = lines[lines.length - 2].replace(/data:/g, '');

    if (
      lastLine &&
      lastLine.trim().startsWith('{') &&
      lastLine.trim().endsWith('}')
    ) {
      try {
        const result = JSON.parse(lastLine);
        const prevResult = JSON.parse(prevLine);

        if (result?.message) {
          return null;
        }

        return prevResult.status === result.status ? null : result.status;
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
        return null;
      }
    }
  }
}

/**
 * CRED.1. The goals query, plus the two credential fields.
 *
 * HAND-WRITTEN, and only until the next codegen run. The generated `GetGoals`
 * document embeds `goalsFields` with its field names spelled out, so a field
 * added to that fragment is not actually requested until generated.ts is
 * rebuilt -- and this repo's codegen reads the schema from a running gateway,
 * which cannot carry CRED.1 before the subgraph in this same change deploys.
 *
 * Regenerating from the local schema files instead was tried and rejected: it
 * reorders the whole 2,900-line file, which is unreviewable and conflicts with
 * every open PR.
 *
 * The fields ARE in `apps/cpd/src/app/graphql/fragment.graphql`, so the first
 * `npm run generate` against a dev gateway carrying CRED.1 makes this document
 * redundant. Delete it then and put `getGoalsGQL.fetch()` back.
 */
/**
 * CRED.1. The goals mutation, plus the two credential fields ON THE RESPONSE.
 *
 * THIS IS WHY THE DIALOG SHOWED AN EMPTY CREDENTIAL ROW. The write always
 * worked -- the generated mutation passes `$goals` through as one variable, so
 * extra keys reached the server untouched and the row was saved. But the
 * generated document's RESPONSE selects `goalsFields`, which does not yet carry
 * credentialTitle, so the mutation came back without it. The reducer sets the
 * store straight from that response, so the checklist redrew from goals that
 * had no credential on them and rendered "Not needed for your report" over a
 * goal that was already in the database.
 *
 * A read on the next page load was correct, which is what made it look like the
 * save had failed when it had not.
 *
 * Same lifetime as GOALS_WITH_CREDENTIAL below: delete both, and the cast in
 * updateUserGoals, the first time codegen runs against a dev gateway carrying
 * CRED.1.
 */
const UPDATE_GOALS_WITH_CREDENTIAL = gql`
  mutation UpdateGoalsWithCredential($goals: GoalsInput!) {
    updateGoals(goals: $goals) {
      success
      message
      code
      goals {
        jobId
        occupationTitle
        onetCode
        schoolId
        schoolProgramEdLevelId
        schoolProgramId
        schoolProgramTitle
        schoolProgramRetired
        schoolTitle
        licenseId
        certificationId
        certificationTitle
        licenseTitle
        credentialCtid
        credentialTitle
        customOnetCode
        customPathwayId
      }
    }
  }
`;

const GOALS_WITH_CREDENTIAL = gql`
  query GetGoalsWithCredential {
    goals {
      jobId
      occupationTitle
      onetCode
      schoolId
      schoolProgramEdLevelId
      schoolProgramId
      schoolProgramTitle
      schoolProgramRetired
      schoolTitle
      licenseId
      certificationId
      certificationTitle
      licenseTitle
      credentialCtid
      credentialTitle
      customOnetCode
      customPathwayId
    }
  }
`;

/**
 * CRED.1. The two credential fields off a goals payload.
 *
 * A narrow read rather than a cast to `UserGoals`: the two types genuinely do
 * not overlap (`certificationId` is a string on the generated input and a
 * number on the model), so TypeScript rejects that cast and it would be the
 * wrong assertion anyway.
 *
 * Both the effect's merged goals object and the model carry these fields; the
 * generated `GoalsInput` will too, the first time codegen runs against a dev
 * gateway carrying CRED.1. Delete this then.
 */
function credentialFieldsOf(goals: unknown): {
  credentialCtid?: string;
  credentialTitle?: string;
} {
  return (goals ?? {}) as {
    credentialCtid?: string;
    credentialTitle?: string;
  };
}
